import React, { useState, useEffect } from "react";
import moment from "moment";
import { useParams, useHistory } from "react-router-dom";
import { Page } from "components";
import { Card, Row, Col, Spinner, Table, Button, Modal, Badge, Pagination } from "react-bootstrap";
import Header from "../../components/Header/Header";
import dateformat from "utils/dateformat";
import { getAssistenciaById } from "./assistenciasApi";
import ApiGSenha from "services/ApiGSenha";
import {
  FaChevronLeft, FaChevronRight, FaDownload, FaKey, FaHeadset,
  FaMapMarkerAlt, FaUndoAlt, FaShieldAlt, FaList, FaTimes, FaSearch, FaArrowLeft,
  FaCalendarAlt, FaFilter, FaPen
} from "react-icons/fa";

import decodeJwt from "../../hooks/decode";
import {
  ASSISTENCIA_CATEGORY_COLORS,
  ASSISTENCIA_TOTAL_GRADIENT,
  KPI,
  getDashboardCardSurface
} from 'constants/dashboardColors';
import './Assistencias.css';

const PAGE_SIZE = 20;

const buildAssistenciaCategories = ({
  data,
  somaTodasAsChaves,
  somaChavesCobradas,
  somaChavesHelpDesk,
  somaChavesLocSolution,
  somaChavesReentrada,
  somaChavesGarantia,
  nEquipments
}) => [
  { icon: FaKey, label: "Total de chaves", count: somaTodasAsChaves, list: data.allData, color: ASSISTENCIA_CATEGORY_COLORS.total },
  { icon: FaList, label: "Contabilizadas", count: somaChavesCobradas, list: data.filteredData, color: ASSISTENCIA_CATEGORY_COLORS.contabilizadas },
  { icon: FaHeadset, label: "Help Desk", count: somaChavesHelpDesk, list: data.assistData, color: ASSISTENCIA_CATEGORY_COLORS.helpDesk },
  { icon: FaMapMarkerAlt, label: "Loc Solution", count: somaChavesLocSolution, list: data.locData, color: ASSISTENCIA_CATEGORY_COLORS.locSolution },
  ...(somaChavesReentrada > 0 ? [{
    icon: FaUndoAlt,
    label: "Reentrada",
    count: somaChavesReentrada,
    list: data.reentranceAssisData,
    color: ASSISTENCIA_CATEGORY_COLORS.reentrada
  }] : []),
  ...(somaChavesGarantia > 0 ? [{
    icon: FaShieldAlt,
    label: `Garantia (${nEquipments} equip.)`,
    count: somaChavesGarantia,
    list: data.logsInGuarante,
    color: ASSISTENCIA_CATEGORY_COLORS.garantia
  }] : [])
];
const getAssistenciaEmailFilter = (email = "") => {
  if (!email) return "";
  const atIndex = email.indexOf("@");
  return atIndex >= 0 ? email.substring(atIndex) : email;
};

const AssistenciasDetail = () => {
  const { id } = useParams();
  const history = useHistory();

  const userDecode = decodeJwt();
  const isAdmin = userDecode?.permission?.assistencias?.write === true;

  const [configs, setConfigs] = useState(null);

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [modalData, setModalData] = useState(null);

  // Filtro: "mensal" ou "periodo"
  const [viewMode, setViewMode] = useState("mensal");
  const [dateStart, setDateStart] = useState(moment().startOf("month").format("YYYY-MM-DD"));
  const [dateEnd, setDateEnd] = useState(moment().endOf("month").format("YYYY-MM-DD"));
  const [periodData, setPeriodData] = useState(null);
  const [loadingPeriod, setLoadingPeriod] = useState(false);

  const activeConfig = configs || {};

  const formatter = activeConfig.currency
    ? new Intl.NumberFormat(activeConfig.format || "pt-BR", {
        style: "currency",
        currency: activeConfig.currency,
      })
    : { format: (v) => `R$ ${(v || 0).toFixed(2)}` };

  useEffect(() => {
    loadAssistenciaData();
  }, [id]);

  const loadAssistenciaData = async () => {
    try {
      setLoading(true);
      const assistencia = await getAssistenciaById(id);

      if (!assistencia?._id) {
        setConfigs(null);
        setData([]);
        return;
      }

      setConfigs(assistencia);
      await loadData(assistencia);
    } catch (error) {
      console.error("Erro ao carregar configuração da assistência:", error);
      setConfigs(null);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const loadData = async (assistencia) => {
    try {
      const currMonth = moment().month();
      const allData = [];

      for (let index = 0; index < 12; index++) {
        const m = moment().month(currMonth - index);
        if (m.year() < 2023) continue;

        const { data } = await ApiGSenha.get("/log/password/assistencia", {
          params: {
            date: `${m.year()}-${m.month()}`,
            email: getAssistenciaEmailFilter(assistencia.email),
            hp: assistencia.emailhp || "",
          },
        });
        allData.push({ values: m, data });
      }

      setData(allData);
    } catch (error) {
      console.error("Erro ao carregar assistencias:", error);
    }
  };

  const canGoBack = currentIndex < data.length - 1;
  const canGoForward = currentIndex > 0;

  const loadPeriodData = async () => {
    if (!configs) return;
    try {
      setLoadingPeriod(true);
      const { data: result } = await ApiGSenha.get("/log/password/assistencia/filter", {
        params: {
          startDate: dateStart,
          endDate: dateEnd,
          email: getAssistenciaEmailFilter(configs.email),
          hp: configs.emailhp || "",
        },
      });

      setPeriodData({
        values: { start: dateStart, end: dateEnd },
        data: result,
      });
    } catch (error) {
      console.error("Erro ao carregar período:", error);
    } finally {
      setLoadingPeriod(false);
    }
  };

  if (!loading && !activeConfig._id) {
    return (
      <Page className="p-3" title="Assistencia">
        <Header page={"Assistencias"} />
        <div className="text-center text-muted mt-5">Assistência não encontrada</div>
      </Page>
    );
  }

  return (
    <Page className="p-3" title={activeConfig.nome || "Assistencia"}>
      <Header page={"Assistencias"} />

      {/* Info da assistência + botão voltar */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div className="d-flex align-items-center gap-3">
          {isAdmin && (
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => history.push("/assistencias")}
              className="d-flex align-items-center gap-2"
            >
              <FaArrowLeft size={12} /> Voltar
            </Button>
          )}
          <div>
            <h5 className="mb-0 fw-bold">{activeConfig.nome}</h5>
            <small className="text-muted">{activeConfig.cidade}/{activeConfig.estado} - {activeConfig.email}</small>
          </div>
        </div>
        <div className="d-flex align-items-center gap-2">
          {isAdmin && (
            <Button
              variant="outline-success"
              size="sm"
              className="btn-theme-outline d-flex align-items-center gap-2"
              onClick={() => history.push(`/assistencias/edit/${id}`)}
            >
              <FaPen size={12} /> Editar
            </Button>
          )}
          <Badge bg={activeConfig.status === "ativo" ? "success" : "secondary"} className="py-2 px-3">
            {activeConfig.status === "ativo" ? "Ativo" : "Inativo"}
          </Badge>
        </div>
      </div>

      {loading ? (
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 400 }}>
          <Spinner animation="border" />
        </div>
      ) : data.length === 0 ? (
        <div className="text-center text-muted mt-5">Nenhum dado encontrado</div>
      ) : (
        <>
          {/* Seletor de modo: Mensal / Período */}
          <ViewModeSelector
            viewMode={viewMode}
            onChangeMode={(mode) => {
              setViewMode(mode);
              setPeriodData(null);
            }}
            dateStart={dateStart}
            dateEnd={dateEnd}
            onChangeDateStart={setDateStart}
            onChangeDateEnd={setDateEnd}
            onSearch={loadPeriodData}
            loadingPeriod={loadingPeriod}
          />

          {viewMode === "mensal" ? (
            <>
              <MonthNavigator
                data={data}
                currentIndex={currentIndex}
                onPrev={() => canGoBack && setCurrentIndex(currentIndex + 1)}
                onNext={() => canGoForward && setCurrentIndex(currentIndex - 1)}
                canGoBack={canGoBack}
                canGoForward={canGoForward}
              />
              <MonthViewer
                key={currentIndex}
                item={data[currentIndex]}
                configs={activeConfig}
                formatter={formatter}
                onOpenDetail={setModalData}
              />
            </>
          ) : loadingPeriod ? (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 300 }}>
              <Spinner animation="border" />
            </div>
          ) : periodData ? (
            <PeriodViewer
              periodData={periodData}
              configs={activeConfig}
              formatter={formatter}
              onOpenDetail={setModalData}
            />
          ) : (
            <div className="text-center text-muted mt-4">
              Selecione o período e clique em buscar
            </div>
          )}
        </>
      )}

      <DetailModal data={modalData} onClose={() => setModalData(null)} />
    </Page>
  );
};

const ViewModeSelector = ({ viewMode, onChangeMode, dateStart, dateEnd, onChangeDateStart, onChangeDateEnd, onSearch, loadingPeriod }) => (
  <Card className="mb-3 border-0 shadow-sm">
    <Card.Body className="py-3 px-4">
      {/* Linha 1: Toggle de modo */}
      <div className="d-flex justify-content-center mb-2">
        <div className="btn-group" role="group">
          <button
            type="button"
            className={`btn assistencias-detail-mode ${viewMode === "mensal" ? "btn-primary" : "btn-outline-primary"}`}
            onClick={() => onChangeMode("mensal")}
            style={{ minWidth: 120 }}
          >
            <FaCalendarAlt size={13} className="me-2" />Mensal
          </button>
          <button
            type="button"
            className={`btn assistencias-detail-mode ${viewMode === "periodo" ? "btn-primary" : "btn-outline-primary"}`}
            onClick={() => onChangeMode("periodo")}
            style={{ minWidth: 120 }}
          >
            <FaFilter size={13} className="me-2" />Período
          </button>
        </div>
      </div>

      {/* Linha 2: Campos de data (só aparece no modo período) */}
      {viewMode === "periodo" && (
        <Row className="g-2 mt-2 align-items-end justify-content-center">
          <Col xs={12} sm={4} md={3}>
            <label className="form-label small text-muted mb-1">Data Inicial</label>
            <input
              type="date"
              className="form-control"
              value={dateStart}
              onChange={(e) => onChangeDateStart(e.target.value)}
              max={dateEnd}
            />
          </Col>
          <Col xs={12} sm={4} md={3}>
            <label className="form-label small text-muted mb-1">Data Final</label>
            <input
              type="date"
              className="form-control"
              value={dateEnd}
              onChange={(e) => onChangeDateEnd(e.target.value)}
              min={dateStart}
            />
          </Col>
          <Col xs={12} sm={4} md={2}>
            <Button
              variant="success"
              onClick={onSearch}
              disabled={loadingPeriod || !dateStart || !dateEnd}
              className="w-100 d-flex align-items-center justify-content-center gap-2"
            >
              {loadingPeriod ? <Spinner animation="border" size="sm" /> : <FaSearch size={14} />}
              Buscar
            </Button>
          </Col>
        </Row>
      )}
    </Card.Body>
  </Card>
);

const PeriodViewer = ({ periodData, configs, formatter, onOpenDetail }) => {
  const { data } = periodData;
  const startFormatted = moment(periodData.values.start).format("DD/MM/YYYY");
  const endFormatted = moment(periodData.values.end).format("DD/MM/YYYY");

  const somaTodasAsChaves = data.allData?.length || 0;
  const somaChavesCobradas = data.filteredData?.length || 0;
  const somaChavesHelpDesk = data.assistData?.length || 0;
  const somaChavesReentrada = data.reentranceAssisData?.length || 0;
  const somaChavesLocSolution = data.locData?.length || 0;
  const somaChavesGarantia = data.logsInGuarante?.length || 0;

  const serialNumbers = [...new Set((data.logsInGuarante || []).map((i) => i.serialnumber))];
  const nEquipments = serialNumbers.length;

  let somaChaves = somaChavesCobradas - somaChavesHelpDesk - somaChavesLocSolution;
  let somachavesExtraValue = 0;
  if (configs.desconto) {
    if (somaChaves > 300) {
      somachavesExtraValue = somaChaves - 300;
      somaChaves = 300;
    }
  }

  const total =
    (somaChaves < 300 && configs.desconto
      ? 30000
      : somaChaves * (configs.desconto ? configs.valor - configs.desconto : configs.valor)) +
    somachavesExtraValue * configs.valor +
    somaChavesHelpDesk * configs.valorhp +
    somaChavesLocSolution * configs.valorloc +
    somaChavesReentrada * (configs.valor - configs.valorhp);

  const categories = buildAssistenciaCategories({
    data,
    somaTodasAsChaves,
    somaChavesCobradas,
    somaChavesHelpDesk,
    somaChavesLocSolution,
    somaChavesReentrada,
    somaChavesGarantia,
    nEquipments
  });

  const downloadParams = new URLSearchParams({
    startDate: periodData.values.start,
    endDate: periodData.values.end,
    email: getAssistenciaEmailFilter(configs.email),
    hp: configs.emailhp || "",
  }).toString();
  const downloadUrl = `${process.env.REACT_APP_API_GERA_SENHA}/log/password/assistencia/filter/download?${downloadParams}`;

  return (
    <div>
      {/* Título do período */}
      <div className="text-center my-3">
        <h5 className="mb-0 fw-bold">{startFormatted} — {endFormatted}</h5>
        <small className="text-muted">Período personalizado</small>
      </div>

      {/* Card total */}
      <Card className="mb-3 border-0 shadow-sm" style={{ background: ASSISTENCIA_TOTAL_GRADIENT }}>
        <Card.Body className="d-flex justify-content-between align-items-center py-3 px-4">
          <div>
            <div className="text-white-50 small fw-bold text-uppercase">Total do Período</div>
            <div className="text-white fw-bold" style={{ fontSize: "1.8rem" }}>
              {formatter.format(total)}
            </div>
          </div>
          <Button
            variant="light"
            size="sm"
            href={downloadUrl}
            target="_blank"
            className="d-flex align-items-center gap-2"
          >
            <FaDownload /> Download
          </Button>
        </Card.Body>
      </Card>

      {/* Cards de categorias */}
      <Row className="g-2 mb-3">
        {categories.map((cat, idx) => (
          <Col xs={6} md={4} lg={3} key={idx}>
            <CategoryCard
              icon={cat.icon}
              label={cat.label}
              count={cat.count}
              color={cat.color}
              onClick={() => cat.count > 0 && onOpenDetail({ title: cat.label, list: cat.list, color: cat.color })}
              disabled={cat.count === 0}
            />
          </Col>
        ))}
      </Row>

      {/* Detalhamento de valores */}
      <Card
        className="border-0 shadow-sm"
        style={{ backgroundColor: "var(--card-bg)", border: "1px solid var(--card-border)" }}
      >
        <Card.Header className="border-bottom fw-bold py-3" style={{ backgroundColor: "var(--card-header-bg)", color: "var(--text-color)" }}>
          Detalhamento de Valores
        </Card.Header>
        <Card.Body className="p-0" style={{ backgroundColor: "var(--card-bg)" }}>
          <Table
            className="mb-0"
            style={{ fontSize: "0.88rem", color: "var(--text-color)", backgroundColor: "transparent" }}
          >
            <tbody>
              <ValueRow
                label={`Chaves assistência (${configs.desconto && somaChaves < 300
                  ? somaChaves
                  : `${somaChaves} x ${formatter.format(configs.desconto ? configs.valor - configs.desconto : configs.valor)}`})`}
                value={configs.desconto && somaChaves < 300
                  ? formatter.format(30000)
                  : formatter.format(somaChaves * (configs.desconto ? configs.valor - configs.desconto : configs.valor))}
              />
              {somachavesExtraValue !== 0 && (
                <ValueRow
                  label={`Chaves sem desconto (${somachavesExtraValue} x ${formatter.format(configs.valor)})`}
                  value={formatter.format(somachavesExtraValue * configs.valor)}
                />
              )}
              {somaChavesHelpDesk !== 0 && (
                <ValueRow
                  label={`Chaves help desk (${somaChavesHelpDesk} x ${formatter.format(configs.valorhp)})`}
                  value={formatter.format(somaChavesHelpDesk * configs.valorhp)}
                />
              )}
              {somaChavesLocSolution !== 0 && (
                <ValueRow
                  label={`Chaves Loc Solution (${somaChavesLocSolution} x ${formatter.format(configs.valorloc)})`}
                  value={formatter.format(somaChavesLocSolution * configs.valorloc)}
                />
              )}
              {somaChavesReentrada !== 0 && (
                <ValueRow
                  label={`Diferença reentrada (${somaChavesReentrada} x ${formatter.format(configs.valor - configs.valorhp)})`}
                  value={formatter.format(somaChavesReentrada * (configs.valor - configs.valorhp))}
                />
              )}
              <tr style={{ backgroundColor: `${KPI.primary}22`, borderTop: "1px solid var(--card-border)" }}>
                <td className="fw-bold py-2 px-3" style={{ color: "var(--text-color)", backgroundColor: "transparent" }}>Total</td>
                <td className="text-end fw-bold py-2 px-3" style={{ color: KPI.primary, backgroundColor: "transparent" }}>
                  {formatter.format(total)}
                </td>
              </tr>
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    </div>
  );
};

const MonthNavigator = ({ data, currentIndex, onPrev, onNext, canGoBack, canGoForward }) => {
  const current = data[currentIndex];
  const monthName = Intl.DateTimeFormat("pt-BR", { month: "long", year: "numeric" }).format(
    new Date(current.values.year(), current.values.month())
  );

  return (
    <div className="d-flex justify-content-center align-items-center my-3 gap-3">
      <Button
        variant="link"
        className="p-2 text-decoration-none"
        disabled={!canGoBack}
        onClick={onPrev}
        style={{ opacity: canGoBack ? 1 : 0.3 }}
      >
        <FaChevronLeft size={20} />
      </Button>

      <div className="text-center" style={{ minWidth: 200 }}>
        <h5 className="mb-0 text-capitalize fw-bold">{monthName}</h5>
        <small className="text-muted">
          {currentIndex === 0 ? "Mês atual" : `${currentIndex} ${currentIndex === 1 ? "mês atrás" : "meses atrás"}`}
        </small>
      </div>

      <Button
        variant="link"
        className="p-2 text-decoration-none"
        disabled={!canGoForward}
        onClick={onNext}
        style={{ opacity: canGoForward ? 1 : 0.3 }}
      >
        <FaChevronRight size={20} />
      </Button>
    </div>
  );
};

const MonthViewer = ({ item, configs, formatter, onOpenDetail }) => {
  const { values, data } = item;

  const somaTodasAsChaves = data.allData?.length || 0;
  const somaChavesCobradas = data.filteredData?.length || 0;
  const somaChavesHelpDesk = data.assistData?.length || 0;
  const somaChavesReentrada = data.reentranceAssisData?.length || 0;
  const somaChavesLocSolution = data.locData?.length || 0;
  const somaChavesGarantia = data.logsInGuarante?.length || 0;

  const serialNumbers = [...new Set((data.logsInGuarante || []).map((i) => i.serialnumber))];
  const nEquipments = serialNumbers.length;

  let somaChaves = somaChavesCobradas - somaChavesHelpDesk - somaChavesLocSolution;
  let somachavesExtraValue = 0;
  if (configs.desconto) {
    if (somaChaves > 300) {
      somachavesExtraValue = somaChaves - 300;
      somaChaves = 300;
    }
  }

  const downloadParams = new URLSearchParams({
    date: `${values.year()}-${values.month()}`,
    email: getAssistenciaEmailFilter(configs.email),
    hp: configs.emailhp || "",
  }).toString();
  const downloadUrl = `${process.env.REACT_APP_API_GERA_SENHA}/log/password/assistencia/download?${downloadParams}`;

  const total =
    (somaChaves < 300 && configs.desconto
      ? 30000
      : somaChaves * (configs.desconto ? configs.valor - configs.desconto : configs.valor)) +
    somachavesExtraValue * configs.valor +
    somaChavesHelpDesk * configs.valorhp +
    somaChavesLocSolution * configs.valorloc +
    somaChavesReentrada * (configs.valor - configs.valorhp);

  const categories = buildAssistenciaCategories({
    data,
    somaTodasAsChaves,
    somaChavesCobradas,
    somaChavesHelpDesk,
    somaChavesLocSolution,
    somaChavesReentrada,
    somaChavesGarantia,
    nEquipments
  });

  return (
    <div>
      {/* Card total */}
      <Card className="mb-3 border-0 shadow-sm" style={{ background: ASSISTENCIA_TOTAL_GRADIENT }}>
        <Card.Body className="d-flex justify-content-between align-items-center py-3 px-4">
          <div>
            <div className="text-white-50 small fw-bold text-uppercase">Total do Mês</div>
            <div className="text-white fw-bold" style={{ fontSize: "1.8rem" }}>
              {formatter.format(total)}
            </div>
          </div>
          <Button
            variant="light"
            size="sm"
            href={downloadUrl}
            target="_blank"
            className="d-flex align-items-center gap-2"
          >
            <FaDownload /> Download
          </Button>
        </Card.Body>
      </Card>

      {/* Cards de categorias clicáveis */}
      <Row className="g-2 mb-3">
        {categories.map((cat, idx) => (
          <Col xs={6} md={4} lg={3} key={idx}>
            <CategoryCard
              icon={cat.icon}
              label={cat.label}
              count={cat.count}
              color={cat.color}
              onClick={() => cat.count > 0 && onOpenDetail({ title: cat.label, list: cat.list, color: cat.color })}
              disabled={cat.count === 0}
            />
          </Col>
        ))}
      </Row>

      {/* Detalhamento de valores */}
      <Card
        className="border-0 shadow-sm"
        style={{ backgroundColor: "var(--card-bg)", border: "1px solid var(--card-border)" }}
      >
        <Card.Header className="border-bottom fw-bold py-3" style={{ backgroundColor: "var(--card-header-bg)", color: "var(--text-color)" }}>
          Detalhamento de Valores
        </Card.Header>
        <Card.Body className="p-0" style={{ backgroundColor: "var(--card-bg)" }}>
          <Table
            className="mb-0"
            style={{ fontSize: "0.88rem", color: "var(--text-color)", backgroundColor: "transparent" }}
          >
            <tbody>
              <ValueRow
                label={`Chaves assistência (${configs.desconto && somaChaves < 300
                  ? somaChaves
                  : `${somaChaves} x ${formatter.format(configs.desconto ? configs.valor - configs.desconto : configs.valor)}`})`}
                value={configs.desconto && somaChaves < 300
                  ? formatter.format(30000)
                  : formatter.format(somaChaves * (configs.desconto ? configs.valor - configs.desconto : configs.valor))}
              />
              {somachavesExtraValue !== 0 && (
                <ValueRow
                  label={`Chaves sem desconto (${somachavesExtraValue} x ${formatter.format(configs.valor)})`}
                  value={formatter.format(somachavesExtraValue * configs.valor)}
                />
              )}
              {somaChavesHelpDesk !== 0 && (
                <ValueRow
                  label={`Chaves help desk (${somaChavesHelpDesk} x ${formatter.format(configs.valorhp)})`}
                  value={formatter.format(somaChavesHelpDesk * configs.valorhp)}
                />
              )}
              {somaChavesLocSolution !== 0 && (
                <ValueRow
                  label={`Chaves Loc Solution (${somaChavesLocSolution} x ${formatter.format(configs.valorloc)})`}
                  value={formatter.format(somaChavesLocSolution * configs.valorloc)}
                />
              )}
              {somaChavesReentrada !== 0 && (
                <ValueRow
                  label={`Diferença reentrada (${somaChavesReentrada} x ${formatter.format(configs.valor - configs.valorhp)})`}
                  value={formatter.format(somaChavesReentrada * (configs.valor - configs.valorhp))}
                />
              )}
              <tr style={{ backgroundColor: `${KPI.primary}22`, borderTop: "1px solid var(--card-border)" }}>
                <td className="fw-bold py-2 px-3" style={{ color: "var(--text-color)", backgroundColor: "transparent" }}>Total</td>
                <td className="text-end fw-bold py-2 px-3" style={{ color: KPI.primary, backgroundColor: "transparent" }}>
                  {formatter.format(total)}
                </td>
              </tr>
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    </div>
  );
};

const CategoryCard = ({ icon: Icon, label, count, color, onClick, disabled }) => (
  <Card
    className="border-0 h-100"
    onClick={onClick}
    style={{
      cursor: disabled ? "default" : "pointer",
      opacity: disabled ? 0.5 : 1,
      transition: "transform 0.15s, box-shadow 0.15s",
      borderRadius: '18px',
      borderTop: `4px solid ${color}`,
      ...getDashboardCardSurface(),
    }}
    onMouseEnter={(e) => {
      if (!disabled) {
        e.currentTarget.style.transform = "translateY(-2px)";
        e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.15)";
      }
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = "translateY(0)";
      e.currentTarget.style.boxShadow = "";
    }}
  >
    <Card.Body className="d-flex align-items-center gap-3 py-3 px-3">
      <div
        className="d-flex align-items-center justify-content-center rounded flex-shrink-0"
        style={{ width: 42, height: 42, backgroundColor: `${color}15`, color, borderRadius: 10 }}
      >
        <Icon size={18} />
      </div>
      <div className="flex-grow-1">
        <div style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>{label}</div>
        <div className="fw-bold" style={{ fontSize: "1.3rem", color: "var(--text-color)" }}>{count}</div>
      </div>
      {!disabled && <FaChevronRight size={12} style={{ color: "var(--text-muted)" }} />}
    </Card.Body>
  </Card>
);

const ValueRow = ({ label, value }) => (
  <tr style={{ borderBottom: "1px solid var(--card-border)" }}>
    <td className="py-2 px-3" style={{ color: "var(--text-color)", backgroundColor: "transparent" }}>{label}</td>
    <td className="text-end fw-bold py-2 px-3" style={{ color: "var(--text-color)", backgroundColor: "transparent" }}>{value}</td>
  </tr>
);

const DetailModal = ({ data, onClose }) => {
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");

  useEffect(() => {
    setPage(0);
    setSearch("");
  }, [data]);

  if (!data) return null;

  const filtered = (data.list || []).filter((item) =>
    !search || [item.serialnumber, item.name, item.email].some(
      (v) => v && v.toLowerCase().includes(search.toLowerCase())
    )
  );

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const pageItems = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  return (
    <Modal show={!!data} onHide={onClose} size="lg" centered scrollable>
      <Modal.Header className="border-0 pb-0">
        <div className="w-100">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div className="d-flex align-items-center gap-2">
              <h5 className="mb-0 fw-bold">{data.title}</h5>
              <Badge bg="secondary" pill>{filtered.length}</Badge>
            </div>
            <Button variant="link" className="text-muted p-0" onClick={onClose}>
              <FaTimes size={18} />
            </Button>
          </div>

          <div className="position-relative">
            <FaSearch
              size={14}
              className="position-absolute text-muted"
              style={{ left: 12, top: "50%", transform: "translateY(-50%)" }}
            />
            <input
              type="text"
              className="form-control form-control-sm ps-4"
              placeholder="Buscar por serial, nome ou email..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              style={{ borderRadius: 8, backgroundColor: "#f8f9fa" }}
            />
          </div>
        </div>
      </Modal.Header>

      <Modal.Body className="p-0">
        {pageItems.length === 0 ? (
          <div className="text-center text-muted py-5">Nenhum registro encontrado</div>
        ) : (
          <Table hover className="mb-0" style={{ fontSize: "0.85rem" }}>
            <thead className="table-light sticky-top">
              <tr>
                <th className="px-3 py-2">#</th>
                <th className="px-3 py-2">Serial</th>
                <th className="px-3 py-2">Data</th>
                <th className="px-3 py-2">Nome</th>
                <th className="px-3 py-2">Email</th>
              </tr>
            </thead>
            <tbody>
              {pageItems.map((item, idx) => (
                <tr key={idx}>
                  <td className="px-3 text-muted">{page * PAGE_SIZE + idx + 1}</td>
                  <td className="px-3">
                    <span className="fw-bold" style={{ fontFamily: "monospace" }}>{item.serialnumber}</span>
                  </td>
                  <td className="px-3">{dateformat(item.data, true)?.replace(",", "")}</td>
                  <td className="px-3">{item.name}</td>
                  <td className="px-3 text-muted">{item.email}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Modal.Body>

      {totalPages > 1 && (
        <Modal.Footer className="border-0 justify-content-between py-2">
          <small className="text-muted">
            {page * PAGE_SIZE + 1}-{Math.min((page + 1) * PAGE_SIZE, filtered.length)} de {filtered.length}
          </small>
          <Pagination size="sm" className="mb-0">
            <Pagination.Prev disabled={page === 0} onClick={() => setPage(page - 1)} />
            {[...Array(Math.min(totalPages, 5))].map((_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i;
              } else if (page < 3) {
                pageNum = i;
              } else if (page > totalPages - 4) {
                pageNum = totalPages - 5 + i;
              } else {
                pageNum = page - 2 + i;
              }
              return (
                <Pagination.Item
                  key={pageNum}
                  active={pageNum === page}
                  onClick={() => setPage(pageNum)}
                >
                  {pageNum + 1}
                </Pagination.Item>
              );
            })}
            <Pagination.Next disabled={page === totalPages - 1} onClick={() => setPage(page + 1)} />
          </Pagination>
        </Modal.Footer>
      )}
    </Modal>
  );
};

export default AssistenciasDetail;
